import { ref } from 'vue'
import { saveAs } from 'file-saver'
import { storeToRefs } from 'pinia'
import { useFabricStore, useTemplatesStore } from '@/store'
import { WorkSpaceThumbType, WorkSpaceClipType, WorkSpaceCommonType, WorkSpaceSafeType, propertiesToInclude } from '@/configs/canvas'
import { ImageFormat } from 'fabric'
import { downloadSVGFile } from '@/utils/download'
import { changeDpiDataUrl } from 'changedpi'
import useCanvas from '@/views/Canvas/useCanvas'
import useCenter from '@/views/Canvas/useCenter'
import { ElementNames } from '@/types/elements'
import {Canvas } from 'fabric'
import { toRaw } from 'vue';
// PDFKit will be loaded dynamically when needed to reduce initial bundle size
// @ts-ignore
const PDFDocument = null; // Placeholder, will be imported dynamically
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import { Buffer } from 'buffer';
import  SVGtoPDF  from 'svg-to-pdf-core';
import blobStream from 'blob-stream';
import {isMobile} from "@/utils/common";
const ligatureMap = {
  "\uFB00": "ff",   // Ligature "ﬀ"
  "\uFB01": "fi",   // Ligature "ﬁ"
  "\uFB02": "fl",   // Ligature "ﬂ"
  "\uFB03": "ffi",  // Ligature "ﬃ"
  "\uFB04": "ffl",  // Ligature "ﬄ"
  "\uFB05": "ft",   // Ligature "ﬅ"
  "\uFB06": "st",   // Ligature "ﬆ"
  "\u00C6": "AE",   // Latin capital ligature AE "Æ"
  "\u00E6": "ae",   // Latin small ligature ae "æ"
  "\u0152": "OE",   // Latin capital ligature OE "Œ"
  "\u0153": "oe",   // Latin small ligature oe "œ"
  "\u0132": "IJ",   // Latin capital ligature IJ "Ĳ"
  "\u0133": "ij",   // Latin small ligature ij "ĳ"
  "\uFB13": "և",    // Armenian ligature "և"
  "\uFB14": "և",    // Armenian ligature "և"
  "\uFB15": "և",    // Armenian ligature "և"
  "\uFB16": "և",    // Armenian ligature "և"
  "\uFB17": "և"     // Armenian ligature "և"
  // Thêm các ligature khác vào đây
};
function replaceLigatures(text) {
  return text.replace(/[\uFB00-\uFB06\u00C6\u00E6\u0152\u0153\u0132\u0133\uFB13-\uFB17]/g, function(match) {
    return ligatureMap[match] || match;
  });
}
export default () => {
  const templatesStore = useTemplatesStore()

  const Exporting = ref(false)
  const { showClip, showSafe } = storeToRefs(useFabricStore())
  const { currentTemplate } = storeToRefs(useTemplatesStore())
  // Export图片
  const exportImage = (format: ImageFormat, quality: number, dpi: number, ignoreClip = true) => {
    Exporting.value = true
    const [ canvas ] = useCanvas()
    const { left, top, width, height } = useCenter()
    const zoom = canvas.getZoom()
    const viewportTransform = canvas.viewportTransform
    const activeObject = canvas.getActiveObject()
    let ignoreObjects = canvas.getObjects().filter(obj => WorkSpaceCommonType.includes(obj.id))
    if (format === 'jpeg') {
      ignoreObjects = canvas.getObjects().filter(obj => WorkSpaceThumbType.includes(obj.id))
    }
    if (ignoreClip) {
      ignoreObjects.map(item => item.set({visible: false}))
      canvas.renderAll()
    }
    if (activeObject) canvas.discardActiveObject()
    canvas.getObjects().filter(item => item.type === ElementNames.REFERENCELINE && item.visible === true).map(item => item.set({visible: false}))
    canvas.set({background: 'rgba(255,255,255,0)'})
    canvas.renderAll()
    let result = canvas.toDataURL({
      multiplier: 1 / zoom,
      // multiplier: 2,
      quality: quality,
      format: format,
      width: width * zoom,
      height: height * zoom,
      left: left * zoom + viewportTransform[4],
      top: top * zoom + viewportTransform[5]
    })
    result = changeDpiDataUrl(result, dpi)
    saveAs(result, `Pdf-Editor-${Date.now()}.${format}`)
    Exporting.value = false
    ignoreObjects.map(item => item.set({visible: true}))
    canvas.getObjects().filter(obj => obj.id === WorkSpaceClipType).map(item => item.set({visible: showClip.value}))
    canvas.getObjects().filter(obj => obj.id === WorkSpaceSafeType).map(item => item.set({visible: showSafe.value}))
    if (activeObject) canvas.setActiveObject(activeObject)
    canvas.getObjects().filter(item => item.type === ElementNames.REFERENCELINE && item.visible === false).map(item => item.set({visible: true}))
    canvas.renderAll()
  }

  const getFontData = async () => {
    const fontsProxy = await templatesStore.getFonts();
    return toRaw(fontsProxy);
  }
  const getSVGData = async (rangeType) => {
    const temps = await templatesStore.getTemplates(rangeType);

    // Use Promise.all with map to ensure the order is maintained
    const svgStrings = await Promise.all(
        temps.map(template => {
          return new Promise((resolve) => {
            const canvas = new Canvas('');
            (canvas as any).loadFromJSON(template).then(() => {
              // CRITICAL: Reorder watermarks to be behind all content
              const watermarks = (canvas as any).getObjects().filter((obj: any) => 
                obj.id?.toString().startsWith('watermark-') || obj.isWatermark
              );
              
              // Send each watermark to back
              watermarks.forEach((watermark: any) => {
                (canvas as any).sendObjectToBack(watermark);
              });
              
              // Ensure workspace is at the very back
              const workspace = (canvas as any).getObjects().find((obj: any) => obj.id === 'WorkSpaceDrawType');
              if (workspace) {
                (canvas as any).sendObjectToBack(workspace);
              }
              
              resolve((canvas as any).toSVG());
              (canvas as any).dispose();
            });
          });
        })
    );

    return svgStrings;  // The array order is now guaranteed
  };

  const exportSVG = () => {
    const [ canvas ] = useCanvas()
    const ignoreObjects = canvas.getObjects().filter(obj => WorkSpaceThumbType.includes(obj.id))
    ignoreObjects.map(item => item.set({visible: false}))
    canvas.renderAll()
    const data = getSVGData()
    downloadSVGFile(data, `Pdf-Editor-${Date.now()}.svg`)
    ignoreObjects.map(item => item.set({visible: true}))
    canvas.getObjects().filter(obj => obj.id === WorkSpaceClipType).map(item => item.set({visible: showClip.value}))
    canvas.getObjects().filter(obj => obj.id === WorkSpaceSafeType).map(item => item.set({visible: showSafe.value}))
    canvas.renderAll()
  }

  // ExportPDF
  const exportPDF = async (rangeType, save = true) => {
    return convertFile('pdf', rangeType.value, save)
  }
  function uint8ArrayToBase64(uint8Array) {
    let binary = '';
    const len = uint8Array.length;
    const chunkSize = 0x8000; // 32KB chunks

    for (let i = 0; i < len; i += chunkSize) {
      const chunk = uint8Array.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, chunk);
    }

    return btoa(binary);
  }
  // ExportPSD
  const exportPSD = async () => {
    convertFile('psd')
  }
  const chunkArray = (array, chunkSize) => {
    const chunks = [];
    for (let i = 0; i < array.length; i += chunkSize) {
      chunks.push(array.slice(i, i + chunkSize));
    }
    return chunks;
  };
  function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
  const convertFile = async (filetype: string, rangeType = 'all', save = true) => {
    // Dynamic import inside the function to ensure it's client-side only and code-split
    const { default: PDFDocumentConstructor } = await import('pdfkit/js/pdfkit.standalone')

    Exporting.value = true;
    if(isMobile()){
      await sleep(3000);
    }
    let fontCache = await getFontData();
    let templates = await templatesStore.getTemplates(rangeType);  // Get the templates directly

    if (templates.length > 200) {
      const parts = Math.ceil(templates.length / 25);
      let pdfBlobs: Blob[] = [];

      for (let i = 0; i < parts; i++) {
        const start = i * 25;
        const end = start + 25;
        const templatePart = templates.slice(start, end);
        const templateData = JSON.stringify(templatePart);

        // Lưu template tạm thời lên S3
        const uniqueFileName = `${uuidv4()}.json`;
        const presignResponse = await fetch(
            `https://s3mfi3z2l4.execute-api.ap-northeast-1.amazonaws.com/default/getIP?key=temp/${uniqueFileName}`
        );
        const { uploadURL, key } = await presignResponse.json();

        await fetch(uploadURL, {
          method: 'PUT',
          body: templateData,
          headers: {
            'Content-Type': 'application/json',
          },
        });

        const fileUrl = `https://saypdf-convert-pdf-to-word.s3.us-west-2.amazonaws.com/${key}`;

        // Tạo đối tượng JSON để gửi dữ liệu font và fileUrl
        const jsonPayload: any = {
          fileUrl: fileUrl,
          fonts: {}
        };

        // Đảm bảo font không bị rỗng và chuyển đổi sang base64
        // Convert fontCache to entries if it's not a Map
        const fontEntries = fontCache instanceof Map 
          ? fontCache.entries() 
          : Object.entries(fontCache || {});
        for (let [fontFamily, fontData] of fontEntries) {
          jsonPayload.fonts[fontFamily] = uint8ArrayToBase64(new Uint8Array(fontData));
        }

        // Gửi yêu cầu tới Lambda và xử lý phản hồi JSON
        const response = await axios.post(
            'https://g9uxemtuv2.execute-api.ap-northeast-1.amazonaws.com/pdf/pdf',
            JSON.stringify(jsonPayload),
            {
              headers: { 'Content-Type': 'application/json' }
            }
        );

        // Kiểm tra phản hồi và xử lý body base64
        if (response.data && response.data.statusCode === 200) {
          const base64Pdf = response.data.body;
          const contentType = response.data.headers['Content-type'] || 'application/pdf';
          const byteCharacters = atob(base64Pdf);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: contentType });

          pdfBlobs.push(blob);
        } else {
          console.error('Error: API response was not successful.');
        }
      }

      const mergedPdfBlob = await mergePdfs(pdfBlobs);
      if (save) {
        saveAs(mergedPdfBlob, 'exported-file.pdf');
      }
      Exporting.value = false;
      return mergedPdfBlob;
    }

    // Original code for when templates length <= 15
    let svgs = await getSVGData(rangeType);
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(<string>svgs[0], "image/svg+xml");
    const svgElement = svgDoc.documentElement;

    const width = svgElement.getAttribute("width") || "612";
    const height = svgElement.getAttribute("height") || "792";

    // Dynamically import PDFKit is now handled at the function start
    
    var doc = new PDFDocumentConstructor({compress: true, size: [parseFloat(width), parseFloat(height)]});
    // Convert fontCache to entries if it's not a Map
    const fontEntries = fontCache instanceof Map 
      ? fontCache.entries() 
      : Object.entries(fontCache || {});
    for (let [fontFamily, fontData] of fontEntries) {
      doc.registerFont(fontFamily, fontData);
    }

    let stream = doc.pipe(blobStream());

    // Loop through all SVGs and add each one as a new page in the PDF
    svgs.forEach((svg, index) => {
      svg = replaceLigatures(svg);
      if (index > 0) {
        doc.addPage({ size: [parseFloat(width), parseFloat(height)] });
      }
      SVGtoPDF(doc, svg, 0, 0, { preserveAspectRatio: 'xMinYMin meet' });
    });

    // Finalize the PDF and end the document
    doc.end();

    // When the PDF generation is complete, create a download link
    return new Promise((resolve, reject) => {
      stream.on('finish', function() {
        // const url = stream.toBlobURL('application/pdf');
        const blob = stream.toBlob('application/pdf');
        if (save) {
           saveAs(blob, 'file.pdf');
        }
        Exporting.value = false;
        resolve(blob);
      });
      stream.on('error', (err) => {
        Exporting.value = false;
        reject(err);
      });
    });
  };


  const mergePdfs = async (pdfBlobs: Blob[]) => {
    const pdfLib = await import('pdf-lib');
    const { PDFDocument } = pdfLib;

    const mergedPdf = await PDFDocument.create();

    for (const pdfBlob of pdfBlobs) {
      const pdfBytes = await pdfBlob.arrayBuffer();
      const pdf = await PDFDocument.load(pdfBytes);
      const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      copiedPages.forEach((page) => mergedPdf.addPage(page));
    }

    const mergedPdfBytes = await mergedPdf.save();
    return new Blob([mergedPdfBytes], { type: 'application/pdf' });
  };


  // Exportjson
  const exportJSON = () => {
    // const serializer = getJSONData()
    // const blob = new Blob([JSON.stringify(serializer)])
    // saveAs(blob, `Pdf-Editor-${Date.now()}.json`)
  }

  return {
    exportImage,
    exportPDF,
    exportPSD,
    // exportJSON,
    exportSVG,
    // getJSONData,
    getSVGData,
    Exporting
  }
}